import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { AppDispatch } from '../../store/store';
import {
  getInvoices,
  recordPayment,
  voidInvoice,
  resendInvoice,
  sendReminder,
  generateInvoices,
  type InvoiceRecord,
  type ArSummary,
} from '../../rtk-apis/dashboard/billing';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { DASHBOARD } from '../../constant';
import { getToken } from '../../Utils/authUtils';

/* ════════════════════════════════════════════════════════════════════════
   Billing — the invoice register and accounts receivable.

   The dashboard above answers "what did we earn". This answers the two
   questions that actually matter to a business: what has been billed, and
   what has been paid. An invoice here is an accounting record with a frozen
   amount, not a rendering of live data — so a resend reproduces the original
   figures rather than today's.
   ════════════════════════════════════════════════════════════════════════ */

const money = (n: number, currency = 'AUD') => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return `${currency} ${v.toFixed(2)}`;
  }
};

const fmtDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const cardSx = {
  background: `linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))`,
  border: `1px solid ${T.line}`,
  borderRadius: '16px',
  p: '18px 20px',
  minWidth: 0,
};

/** Status colour. Overdue is derived from the balance and due date, not stored. */
const statusStyle = (inv: InvoiceRecord) => {
  if (inv.voided) {
    return { label: 'Void', color: T.ink3 };
  }
  if (inv.status === 'PAID') {
    return { label: 'Paid', color: T.green };
  }
  if (inv.daysOverdue > 0) {
    return { label: `Overdue ${inv.daysOverdue}d`, color: T.red };
  }
  if (inv.status === 'PART_PAID') {
    return { label: 'Part paid', color: T.amber };
  }
  if (inv.status === 'SENT') {
    return { label: 'Sent', color: T.sky };
  }
  return { label: 'Draft', color: T.ink3 };
};

const GRID = '1.05fr 1.4fr 0.95fr 0.8fr 0.8fr 0.8fr 0.95fr 1fr';

interface Props {
  year: number;
  onChanged?: () => void;
  notify?: (msg: string, sev: 'success' | 'error') => void;
}

const BillingPanel: React.FC<Props> = ({ year, onChanged, notify }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [ar, setAr] = useState<ArSummary | null>(null);
  const [collected, setCollected] = useState<{ collected: number; payments: number } | null>(null);
  const [filter, setFilter] = useState<'all' | 'outstanding' | 'PAID'>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState<InvoiceRecord | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payMethod, setPayMethod] = useState('Bank transfer');
  const [payRef, setPayRef] = useState('');
  const [workingId, setWorkingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = (await dispatch(
        getInvoices({
          year,
          outstanding: filter === 'outstanding',
          status: filter === 'PAID' ? 'PAID' : undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
        })
      ).unwrap()) as { data?: { invoices: InvoiceRecord[]; ar: ArSummary; collected: never } };
      setInvoices(res?.data?.invoices || []);
      setAr(res?.data?.ar || null);
      setCollected(res?.data?.collected || null);
    } catch (err) {
      console.error('[billing] register fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, year, filter, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const openPay = (inv: InvoiceRecord) => {
    setPaying(inv);
    // Default to settling the invoice in full — the common case — while still
    // allowing a part payment.
    setPayAmount(String(inv.balance));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayMethod('Bank transfer');
    setPayRef('');
  };

  const submitPayment = async () => {
    if (!paying) {
      return;
    }
    try {
      await dispatch(
        recordPayment({
          invoiceId: paying._id,
          amount: Number(payAmount),
          paidOn: payDate,
          method: payMethod,
          reference: payRef,
        })
      ).unwrap();
      setPaying(null);
      notify?.('Payment recorded.', 'success');
      load();
      onChanged?.();
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not record the payment.', 'error');
    }
  };

  const doResend = async (inv: InvoiceRecord) => {
    setWorkingId(inv._id);
    try {
      const res = (await dispatch(resendInvoice({ invoiceId: inv._id })).unwrap()) as {
        msg?: string;
      };
      notify?.(res?.msg || 'Invoice re-sent.', 'success');
      load();
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not re-send.', 'error');
    } finally {
      setWorkingId(null);
    }
  };

  const doRemind = async (inv: InvoiceRecord) => {
    setWorkingId(inv._id);
    try {
      const res = (await dispatch(sendReminder({ invoiceId: inv._id })).unwrap()) as {
        msg?: string;
      };
      notify?.(res?.msg || 'Reminder sent.', 'success');
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not send the reminder.', 'error');
    } finally {
      setWorkingId(null);
    }
  };

  const doVoid = async (inv: InvoiceRecord) => {
    const reason = window.prompt(`Void ${inv.invoiceNumber}? Give a reason for the record:`);
    if (reason === null) {
      return;
    }
    setWorkingId(inv._id);
    try {
      await dispatch(voidInvoice({ invoiceId: inv._id, reason })).unwrap();
      notify?.(`${inv.invoiceNumber} voided.`, 'success');
      load();
      onChanged?.();
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not void.', 'error');
    } finally {
      setWorkingId(null);
    }
  };

  const [generating, setGenerating] = useState(false);

  /**
   * Generate invoices for the exact From/To window currently selected — one per
   * clinic, same duplicate-period protection the automatic scheduler relies on, so
   * running this again for a range already invoiced is a no-op, not a double-bill.
   */
  const doGenerate = async () => {
    if (!fromDate || !toDate) {
      notify?.('Set both From and To first.', 'error');
      return;
    }
    setGenerating(true);
    try {
      const res = (await dispatch(
        generateInvoices({ from: fromDate, to: toDate })
      ).unwrap()) as { msg?: string };
      notify?.(res?.msg || 'Invoices generated.', 'success');
      load();
      onChanged?.();
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not generate invoices.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  /**
   * CSV download. Done with fetch + a blob rather than a plain link because the
   * endpoint needs the bearer token, which an <a href> cannot carry.
   */
  const downloadCsv = async () => {
    try {
      const res = await fetch(`${DASHBOARD.INVOICE_EXPORT}?year=${year}`, {
        headers: { Authorization: 'Bearer ' + getToken() },
      });
      if (!res.ok) {
        throw new Error('export failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cardiachealth-invoices-${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify?.('Could not export the register.', 'error');
    }
  };

  const aging = useMemo(() => {
    if (!ar) {
      return [];
    }
    return [
      { label: 'Not yet due', value: ar.buckets.current, color: T.sky },
      { label: '1–30 days', value: ar.buckets.d1_30, color: T.amber },
      { label: '31–60 days', value: ar.buckets.d31_60, color: '#fb923c' },
      { label: '61–90 days', value: ar.buckets.d61_90, color: T.red },
      { label: '90+ days', value: ar.buckets.d90plus, color: '#e11d48' },
    ];
  }, [ar]);

  const agingTotal = aging.reduce((a, b) => a + b.value, 0);

  return (
    <Box sx={{ mt: '16px' }}>
      {/* receivables summary */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: '14px',
          mb: '14px',
        }}
      >
        {[
          {
            label: 'Outstanding',
            value: money(ar?.outstanding || 0),
            sub: `${ar?.openCount || 0} open invoice${ar?.openCount === 1 ? '' : 's'}`,
            warn: false,
          },
          {
            label: 'Overdue',
            value: money(ar?.overdueTotal || 0),
            sub: `${ar?.overdueCount || 0} past due date`,
            warn: !!ar?.overdueCount,
          },
          {
            label: `Collected · ${year}`,
            value: money(collected?.collected || 0),
            sub: `${collected?.payments || 0} payment${collected?.payments === 1 ? '' : 's'}`,
            warn: false,
          },
          {
            label: 'Invoices',
            value: String(invoices.length),
            sub: 'In the register',
            warn: false,
          },
        ].map(k => (
          <Box
            key={k.label}
            sx={cardSx}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: T.ink3,
              }}
            >
              {k.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 800,
                color: k.warn ? T.red : '#fff',
                mt: '6px',
                lineHeight: 1.1,
              }}
            >
              {k.value}
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.ink3, mt: '2px' }}>{k.sub}</Typography>
          </Box>
        ))}
      </Box>

      {/* aging — a stacked bar reads faster than five numbers */}
      {agingTotal > 0 && (
        <Box sx={{ ...cardSx, mb: '14px' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, mb: '12px' }}>
            Ageing of outstanding balances
          </Typography>
          <Box
            sx={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden', mb: '10px' }}
          >
            {aging
              .filter(a => a.value > 0)
              .map(a => (
                <Tooltip
                  key={a.label}
                  title={`${a.label}: ${money(a.value)}`}
                >
                  <Box sx={{ width: `${(a.value / agingTotal) * 100}%`, background: a.color }} />
                </Tooltip>
              ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {aging.map(a => (
              <Box
                key={a.label}
                sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: a.color }} />
                <Typography sx={{ fontSize: 12, color: T.ink3 }}>
                  {a.label} · <b style={{ color: T.ink }}>{money(a.value)}</b>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* register */}
      <Box sx={{ ...cardSx, p: '18px 20px 8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: '12px',
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
            Invoice register
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={downloadCsv}
              sx={{ color: T.ink3, textTransform: 'none' }}
            >
              Export CSV
            </Button>
            <TextField
              label="From"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              sx={{ width: 150 }}
            />
            <TextField
              label="To"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              sx={{ width: 150 }}
            />
            {(fromDate || toDate) && (
              <Button
                size="small"
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                }}
                sx={{ color: T.ink3, textTransform: 'none' }}
              >
                Clear
              </Button>
            )}
            {fromDate && toDate && (
              <Button
                size="small"
                variant="outlined"
                onClick={doGenerate}
                disabled={generating}
                sx={{ color: T.teal, borderColor: T.teal, textTransform: 'none' }}
              >
                {generating ? 'Generating…' : 'Generate Invoices'}
              </Button>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_, v) => v && setFilter(v)}
            >
              <ToggleButton value="all">All</ToggleButton>
              <Tooltip title="Issued and not yet paid. Drafts are excluded — they have not been sent to anyone.">
                <ToggleButton value="outstanding">Outstanding</ToggleButton>
              </Tooltip>
              <ToggleButton value="PAID">Paid</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 980 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: '12px',
                pb: '8px',
                borderBottom: `1px solid ${T.line}`,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: T.ink3,
              }}
            >
              <span>Invoice</span>
              <span>Clinic</span>
              <span>Period</span>
              <span>Due</span>
              <span>Total</span>
              <span>Balance</span>
              <span>Status</span>
              <span style={{ textAlign: 'right' }}>Actions</span>
            </Box>

            {invoices.map(inv => {
              const st = statusStyle(inv);
              return (
                <Box
                  key={inv._id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    gap: '12px',
                    alignItems: 'center',
                    py: '11px',
                    borderBottom: `1px solid ${T.line}`,
                    fontSize: 13,
                    color: T.ink,
                    opacity: inv.voided ? 0.55 : 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                      {inv.invoiceNumber}
                    </Typography>
                    {inv.source === 'scheduled' && (
                      <Typography sx={{ fontSize: 10.5, color: T.ink3 }}>Automatic</Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: T.ink2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {inv.clinicName}
                  </Typography>
                  <span style={{ color: T.ink3 }}>{inv.periodLabel}</span>
                  <span style={{ color: inv.daysOverdue > 0 ? T.red : T.ink3 }}>
                    {fmtDate(inv.dueOn)}
                  </span>
                  <span style={{ fontWeight: 600 }}>{money(inv.total, inv.currency)}</span>
                  <span
                    style={{
                      fontWeight: 700,
                      color: inv.balance > 0 ? (inv.daysOverdue > 0 ? T.red : '#fff') : T.green,
                    }}
                  >
                    {money(inv.balance, inv.currency)}
                  </span>
                  <Box>
                    <Chip
                      size="small"
                      label={st.label}
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        color: st.color,
                        background: `${st.color}1f`,
                        border: `1px solid ${st.color}66`,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: '2px', justifySelf: 'end' }}>
                    <Tooltip title={inv.balance > 0 ? 'Record a payment' : 'Settled'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={inv.voided || inv.balance <= 0}
                          onClick={() => openPay(inv)}
                          sx={{ color: inv.balance > 0 ? T.teal : T.ink3 }}
                        >
                          <PaymentsOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        inv.balance > 0 ? 'Send a payment reminder' : 'Nothing outstanding to chase'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={inv.voided || inv.balance <= 0 || workingId === inv._id}
                          onClick={() => doRemind(inv)}
                          sx={{ color: inv.daysOverdue > 0 ? T.amber : T.ink3 }}
                        >
                          <NotificationsActiveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        inv.balance <= 0
                          ? 'Email a copy — the invoice is marked paid, so it goes out as a receipted copy, not a request for payment'
                          : inv.status === 'DRAFT'
                            ? 'Email this invoice to the clinic — it has not been sent yet'
                            : 'Re-send this invoice by email (same amounts as originally issued)'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={inv.voided || workingId === inv._id}
                          onClick={() => doResend(inv)}
                          sx={{ color: T.ink3 }}
                        >
                          {workingId === inv._id ? (
                            <CircularProgress
                              size={15}
                              sx={{ color: T.teal }}
                            />
                          ) : (
                            <SendOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        inv.amountPaid > 0 ? 'Cannot void — payments recorded' : 'Void this invoice'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={inv.voided || inv.amountPaid > 0}
                          onClick={() => doVoid(inv)}
                          sx={{ color: T.ink3 }}
                        >
                          <BlockOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}

            {!invoices.length && !loading && (
              <Typography sx={{ fontSize: 13, color: T.ink3, py: 3, textAlign: 'center' }}>
                No invoices yet. Raise one from the clinic table above.
              </Typography>
            )}
            {loading && !invoices.length && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress
                  size={22}
                  sx={{ color: T.teal }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* record payment */}
      <Dialog
        open={!!paying}
        onClose={() => setPaying(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 17, fontWeight: 700 }}>
          Record payment — {paying?.invoiceNumber}
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
        >
          <Typography sx={{ fontSize: 13, color: T.ink3 }}>
            {paying?.clinicName} · outstanding{' '}
            <b style={{ color: T.ink }}>{money(paying?.balance || 0, paying?.currency)}</b>
          </Typography>
          <TextField
            label="Amount"
            size="small"
            type="number"
            value={payAmount}
            onChange={e => setPayAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            helperText="Less than the balance records a part payment."
          />
          <TextField
            label="Date received"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={payDate}
            onChange={e => setPayDate(e.target.value)}
          />
          <Select
            size="small"
            value={payMethod}
            onChange={e => setPayMethod(e.target.value)}
          >
            {['Bank transfer', 'Credit card', 'Cheque', 'Cash', 'Other'].map(m => (
              <MenuItem
                key={m}
                value={m}
              >
                {m}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label="Reference (optional)"
            size="small"
            value={payRef}
            onChange={e => setPayRef(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPaying(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitPayment}
          >
            Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPanel;
